import { useState } from "react"
import { FileTree, InfoBox, FileExplanationPanel } from "@/components"
import { mongoDBProjectStructure } from "@/data/project-structures"
import { mongoDBFileExplanations, type FileExplanation } from "@/data/file-explanations"

export function MongoDBStructurePage() {
  const [selectedFile, setSelectedFile] = useState<FileExplanation | null>(null)

  const handleFileClick = (filePath: string) => {
    const fileInfo = mongoDBFileExplanations[filePath]
    if (fileInfo) {
      setSelectedFile(fileInfo)
    }
  }

  return (
    <>
      <div className="space-y-10">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold">Project Structure</h1>
          <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
            Understand the codebase organization and explore each file's purpose. 
            Click on any file to see detailed explanations.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <InfoBox type="info" title="Interactive File Explorer">
              Click any file in the tree below to view its purpose, key features, and related files.
            </InfoBox>
            <div className="mt-6">
              <FileTree data={mongoDBProjectStructure} onFileClick={handleFileClick} />
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <ModuleDescription
              title="auth/"
              description="Complete authentication module with JWT, OAuth, guards, and strategies"
              features={[
                "JWT authentication with refresh tokens",
                "OAuth controllers and strategies",
                "Custom decorators (@Public, @CurrentUser)",
                "Authentication guards",
                "Passport strategies",
              ]}
            />
            <ModuleDescription
              title="config/"
              description="Type-safe configuration files for app settings and database connection"
              features={[
                "App configuration (JWT, OAuth, etc.)",
                "Database configuration (MongoDB URI)",
                "Environment-based settings",
              ]}
            />
            <ModuleDescription
              title="shared/"
              description="Reusable components used across the application"
              features={[
                "Global DTOs and response wrappers",
                "Enums for type safety",
                "Exception filters",
                "Guards and interceptors",
                "Utility functions",
              ]}
            />
            <ModuleDescription
              title="user/"
              description="User management module with profile operations and admin features"
              features={[
                "User controller and service",
                "User schemas (User, UserProvider)",
                "Profile management DTOs",
                "Admin operations",
              ]}
            />
            <ModuleDescription
              title="scripts/"
              description="Utility scripts for setup and development workflows"
              features={[
                "Interactive setup script",
                "Environment configuration",
                "JWT secret generation",
              ]}
            />
          </div>
        </div>

        <div className="bg-muted/30 rounded-xl p-8 space-y-6">
          <h2 className="text-2xl font-bold">Module Architecture</h2>
          <p className="text-muted-foreground leading-relaxed">
            The template follows NestJS best practices with a modular architecture. Each module 
            is self-contained with its own controllers, services, DTOs, and schemas. The shared 
            module provides common utilities that can be used across all modules.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-card p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Feature Modules</h4>
              <p className="text-muted-foreground">auth/, user/</p>
            </div>
            <div className="bg-card p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Shared Module</h4>
              <p className="text-muted-foreground">shared/</p>
            </div>
            <div className="bg-card p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Configuration</h4>
              <p className="text-muted-foreground">config/</p>
            </div>
          </div>
        </div>
      </div>

      <FileExplanationPanel 
        file={selectedFile} 
        onClose={() => setSelectedFile(null)} 
      />
    </>
  )
}

function ModuleDescription({ 
  title, 
  description, 
  features 
}: { 
  title: string
  description: string
  features: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div>
        <h3 className="font-mono font-semibold text-lg mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold mb-2">Contains:</h4>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex gap-2 text-sm text-muted-foreground">
              <span className="text-primary">→</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

